import React from 'react';
import { format } from 'date-fns';

interface NotificationPreviewProps {
  title: string;
  subtitle?: string;
  body: string;
  imageUrl?: string;
  type: string;
  category?: string;
  quizData?: any;
  actions?: any[];
}

export default function NotificationPreview({
  title,
  subtitle,
  body,
  imageUrl,
  type,
  category,
  quizData,
  actions,
}: NotificationPreviewProps) {
  return (
    <div className="bg-gray-100 dark:bg-gray-900 rounded-2xl p-6">
      {/* Phone frame */}
      <div className="mx-auto max-w-sm">
        <div className="bg-black rounded-[2.5rem] p-2 shadow-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-[2rem] p-4 min-h-[600px]">
            {/* Status bar */}
            <div className="flex justify-between items-center mb-4 text-xs text-gray-600 dark:text-gray-400">
              <span>{format(new Date(), 'HH:mm')}</span>
              <div className="flex gap-1">
                <div className="w-4 h-3 bg-gray-800 dark:bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-800 dark:bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-800 dark:bg-gray-600 rounded-sm"></div>
              </div>
            </div>

            {/* Notification card */}
            <div className="mt-8">
              <div className="bg-white/90 dark:bg-gray-700/90 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-600/50 overflow-hidden">
                {imageUrl && (
                  <div className="aspect-video bg-gray-200 dark:bg-gray-600">
                    <img
                      src={imageUrl}
                      alt="Notification"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm">🔔</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {title || 'Notification Title'}
                      </h4>
                      {subtitle && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Category badge only for non-quiz types */}
                  {type !== 'quiz' && (
                    <div className="flex gap-2 mb-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {type === 'news' ? '📰' : type === 'achievement' ? '🏆' : type === 'promotion' ? '🎁' : '📢'} {type}
                      </span>
                      {category && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          {category === 'news' ? '📰 News Tab' : 
                           category === 'announcements' ? '📢 Updates Tab' :
                           category === 'achievements' ? '🏆 Wins Tab' :
                           category === 'offers' ? '🎁 Offers Tab' : 
                           '📄 General'}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className={`mb-2 ${type === 'quiz' ? 'text-lg font-bold text-gray-900 dark:text-white leading-tight' : 'text-sm text-gray-600 dark:text-gray-300'}`}>
                    {body ? (
                      <div 
                        className={`prose ${type === 'quiz' ? 'prose-lg' : 'prose-sm'} dark:prose-invert max-w-none ${type === 'quiz' ? 'prose-headings:text-gray-900 dark:prose-headings:text-white' : ''}`}
                        dangerouslySetInnerHTML={{ __html: body }} 
                      />
                    ) : (
                      <p className="italic text-gray-400">Your notification message will appear here...</p>
                    )}
                  </div>

                  {/* Quiz Preview - Show options only, question is already in body */}
                  {type === 'quiz' && quizData?.questions?.length > 0 && (
                    <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="space-y-1">
                        {quizData.questions.slice(0, 1).map((q: any, idx: number) => (
                          <div key={idx} className="text-xs">
                            {q.type === 'multiple_choice' && q.options && (
                              <div className="space-y-1">
                                {q.options.filter((o: string) => o).map((option: string, i: number) => (
                                  <div key={i} className="flex items-center">
                                    <div className="w-3 h-3 border border-gray-400 rounded-full mr-2"></div>
                                    <span className="text-gray-600 dark:text-gray-400">{String.fromCharCode(65 + i)}. {option}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {q.type === 'true_false' && (
                              <div className="space-y-1">
                                <div className="flex items-center">
                                  <div className="w-3 h-3 border border-gray-400 rounded-full mr-2"></div>
                                  <span className="text-gray-600 dark:text-gray-400">A. True</span>
                                </div>
                                <div className="flex items-center">
                                  <div className="w-3 h-3 border border-gray-400 rounded-full mr-2"></div>
                                  <span className="text-gray-600 dark:text-gray-400">B. False</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {quizData.questions.length > 1 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-2">
                            +{quizData.questions.length - 1} more question{quizData.questions.length > 2 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons Preview */}
                  {actions && actions.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {actions.map((action: any, idx: number) => {
                        const bgColor = 
                          action.style === 'primary' ? 'bg-blue-600 text-white' :
                          action.style === 'secondary' ? 'bg-gray-600 text-white' :
                          action.style === 'success' ? 'bg-green-600 text-white' :
                          action.style === 'danger' ? 'bg-red-600 text-white' :
                          'border border-gray-400 text-gray-700';
                        
                        return (
                          <button
                            key={idx}
                            className={`w-full px-3 py-2 rounded-lg text-sm font-medium ${bgColor}`}
                          >
                            {action.label || `Action ${idx + 1}`}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Clarity UPSC</span>
                    <span>Just now</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}